import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import { titleize } from 'underscore.string';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableCell,
  TableRow,
} from '@cargomatic/ui';
import { sluggify } from 'utils/localization/utils';
import CargomaticTooltip from 'components/v2/Tooltip';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import CargomaticSelect from 'components/v2/select';
import { CustomerSearchCombobox } from 'components/design-system/organisms/CustomerSearchCombobox';
import {
  equipmentTypesMapping,
  assetTypes,
  equipmentTypes,
  equipmentSizes,
  CHASSIS_OWNERSHIP_TYPE,
} from 'constants/Equipment';

const DISABLED_ORDER_STATUSES = ['created', 'en_route'];

const disableStatusDropdown = (equipment) => {
  const { order, _equipmentType } = equipment;

  if (_equipmentType && _equipmentType === 'container') {
    if (order) {
      const { order_type, status } = order;

      if (order_type === 'import' && DISABLED_ORDER_STATUSES.includes(status)) {
        return true;
      }
    }
  }

  return false;
};

const EXPORT_DATES = [
  'earliest_empty_date',
  'earliest_return_date',
  'last_cut_date',
];
const IMPORT_DATES = ['last_free_date'];
class EquipmentFormRow extends Component {
  static propTypes = {
    equipment: PropTypes.object.isRequired,
    actionsDisabled: PropTypes.bool.isRequired,
    onDeselect: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
  };

  getValidationClassName(field) {
    const error = this.props.equipment.error || {};

    if (error[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  disableDatePicker = (id) => {
    const { equipment } = this.props;

    if (equipment && equipment.order) {
      const {
        order: { order_type },
      } = equipment;
      if (order_type === 'import') {
        return !IMPORT_DATES.includes(id);
      }
      return !EXPORT_DATES.includes(id);
    }

    return true;
  };

  handleApplyToAll = () => this.props.onApplyToAll(this.props.equipment._id);

  handleChange = (field) => (e) => {
    const value = e.target.value;
    this.props.onFieldChange(this.props.equipment._id, field, value);
  };

  handleSelectChange = (field) => (value) => {
    this.props.onFieldChange(this.props.equipment._id, field, value);
  };

  handleToggleChange = (field) => (value) => {
    this.props.onFieldChange(this.props.equipment._id, field, value);
  };

  handleDateChange = (field) => (date) =>
    this.props.onFieldChange(this.props.equipment._id, field, date);

  handleDeselect = () => this.props.onDeselect(this.props.equipment._id);

  renderEquipmentIdField() {
    const className = {
      ...this.getValidationClassName('equipment_id'),
    };

    const { equipment } = this.props;

    return (
      <div className={classnames(className)}>
        <Input
          type="text"
          className="w-full"
          value={equipment.equipment_id}
          onChange={this.handleChange('equipment_id')}
          placeholder="Enter Equipment Number"
        />
      </div>
    );
  }

  renderAssetTypeField() {
    const { equipment, t } = this.props;

    return (
      <div
        className={classnames(this.getValidationClassName('_equipmentType'))}
      >
        <Select
          value={equipment._equipmentType || ''}
          onValueChange={this.handleSelectChange('_equipmentType')}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('SELECT_ASSET_TYPE')} />
          </SelectTrigger>
          <SelectContent>
            {assetTypes.map((assetType) => (
              <SelectItem key={assetType} value={assetType}>
                {t(assetType.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  renderStatuses() {
    const { equipment, t } = this.props;
    const disabled = disableStatusDropdown(equipment);

    const statusSelect = (
      <Select
        value={equipment.status || ''}
        onValueChange={this.handleSelectChange('status')}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t('SELECT_EQUIPMENT_STATUS')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unavailable">
            {t('UNAVAILABLE', { count: 1 })}
          </SelectItem>
          <SelectItem value="available">
            {t('AVAILABLE', { count: 1 })}
          </SelectItem>
          <SelectItem value="closed_area">{t('CLOSED_AREA')}</SelectItem>
          <SelectItem value="on_hold">{t('ON_HOLD')}</SelectItem>
        </SelectContent>
      </Select>
    );

    return (
      <div className={classnames(this.getValidationClassName('status'))}>
        {disabled ? (
          <CargomaticTooltip position="top" body={() => statusSelect}>
            {t('DISABLED_EQUIPMENT_TOOLTIP')}
          </CargomaticTooltip>
        ) : (
          statusSelect
        )}
      </div>
    );
  }

  renderEquipmentType() {
    const { equipment, t } = this.props;
    const disableEquipmentType =
      equipment?._equipmentType !== equipmentTypesMapping.CONTAINER;
    return (
      <div className={classnames(this.getValidationClassName('_type'))}>
        <Select
          value={equipment._type || ''}
          onValueChange={this.handleSelectChange('_type')}
          disabled={disableEquipmentType}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('SELECT_EQUIPMENT_TYPE')} />
          </SelectTrigger>
          <SelectContent>
            {_.map(equipmentTypes, (label, key) => (
              <SelectItem key={key} value={key}>
                {t(sluggify(label))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  renderToggleSwitch() {
    const { equipment } = this.props;
    const disableIsPrivate =
      equipment?._equipmentType !== equipmentTypesMapping.CHASSIS;
    return (
      <div>
        <ToggleSwitch
          enabled={!!equipment?.is_private}
          disabled={disableIsPrivate}
          onChange={this.handleToggleChange('is_private')}
          data-testid="bulk-equipment-is-private-switch"
        />
      </div>
    );
  }

  renderChassisOwnerType() {
    const { equipment } = this.props;
    return (
      <CargomaticSelect
        selected={equipment?.chassis_owner_type}
        className="mx-1.5 w-full"
        disabled={!equipment?.is_private}
        onSelect={({ value }) => {
          this.props.onFieldChange(
            this.props.equipment._id,
            'chassis_owner_type',
            value,
          );
        }}
        hideLabel
        defaultSelectLabel=""
        items={CHASSIS_OWNERSHIP_TYPE}
        textKey="localizationSlug"
        valueKey="value"
        data-testid="bulk-equipment-owner-type-dropdown"
      />
    );
  }

  renderCustomerSearch() {
    const { equipment, t } = this.props;
    return (
      <CustomerSearchCombobox
        // Remount when the selected customer changes from outside (e.g. Apply
        // To All) so the input re-seeds its label from `value` on mount.
        key={equipment?.chassis_owner?._id || 'none'}
        id="equipment-customer-search"
        disabled={equipment?.chassis_owner_type !== 'customer'}
        value={equipment?.chassis_owner || null}
        onValueChange={(chassis_owner) => {
          this.props.onFieldChange(
            this.props.equipment._id,
            'chassis_owner',
            chassis_owner,
          );
        }}
        placeholder={t('SEARCH_FOR_CUSTOMER')}
        popoverClassName="min-w-[360px]"
      />
    );
  }
  renderEquipmentSizes() {
    const { equipment, t } = this.props;
    const disableEquipmentSize =
      equipment?._equipmentType !== equipmentTypesMapping.CONTAINER;
    return (
      <div
        className={classnames(this.getValidationClassName('_equipmentSize'))}
      >
        <Select
          value={equipment._equipmentSize || ''}
          onValueChange={this.handleSelectChange('_equipmentSize')}
          disabled={disableEquipmentSize}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('SELECT_EQUIPMENT_SIZE')} />
          </SelectTrigger>
          <SelectContent>
            {equipmentSizes.map((size) => (
              <SelectItem key={size} value={size}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  renderColumns() {
    const { equipment } = this.props;

    const { _id: id } = equipment;

    const idKey = `id_${id}`;
    const assetTypeKey = `asset_type_${id}`;
    const statusKey = `status_${id}`;
    const typeKey = `type_${id}`;
    const sizeKey = `size_${id}`;
    const toggleKey = `toggle_${id}`;
    const chassisOwnerTypeKey = `chassis_owner_type_${id}`;
    const customerKey = `chassis_owner_${id}`;
    const applyAllKey = `apply_${id}`;
    const removeKey = `remove_${id}`;

    return [
      <TableCell key={idKey} className="min-w-[150px]">
        {this.renderEquipmentIdField()}
      </TableCell>,
      <TableCell key={assetTypeKey} className="min-w-[150px]">
        {this.renderAssetTypeField()}
      </TableCell>,
      <TableCell key={typeKey} className="min-w-[160px]">
        {this.renderEquipmentType()}
      </TableCell>,
      <TableCell key={sizeKey} className="min-w-[150px]">
        {this.renderEquipmentSizes()}
      </TableCell>,
      <TableCell key={toggleKey} className="min-w-[90px]">
        {this.renderToggleSwitch()}
      </TableCell>,
      <TableCell key={chassisOwnerTypeKey} className="min-w-[170px]">
        {this.renderChassisOwnerType()}
      </TableCell>,
      <TableCell key={customerKey} className="min-w-[240px]">
        {this.renderCustomerSearch()}
      </TableCell>,
      <TableCell key={statusKey} className="min-w-[160px]">
        {this.renderStatuses()}
      </TableCell>,
      <TableCell key={applyAllKey} className="min-w-[110px]">
        {this.renderApplyToAllButton()}
      </TableCell>,
      <TableCell key={removeKey}>{this.renderRemoveActionColumn()}</TableCell>,
    ];
  }

  renderApplyToAllButton() {
    const { actionsDisabled, t } = this.props;
    const disabled = actionsDisabled;

    return (
      <Button
        variant="outline"
        onClick={this.handleApplyToAll}
        disabled={disabled}
      >
        {titleize(t('APPLY'))}
      </Button>
    );
  }

  renderRemoveActionColumn() {
    return (
      <Button
        variant="outline"
        onClick={this.handleDeselect}
        disabled={this.props.actionsDisabled}
      >
        <i className="fa fa-remove" />
      </Button>
    );
  }

  render() {
    return <TableRow>{this.renderColumns()}</TableRow>;
  }
}

export default withTranslation()(EquipmentFormRow);
